import {
  AGENT_ROLES,
  BOARDS_ID,
  FILTER_OPERATOR_ENUMS,
  FILTER_RULE_ENUMS,
} from '@constants';
import { formatDate, sortArray, getCleanedUrlParams } from '@utils';
import { GREEN_CARD_TASK_STATUSES_NAMES } from 'components/greenCard/constants';
import { URLS } from 'layouts/DrawerLayout/constants';
import { groupBy, omit } from 'lodash-es';

export const filterGreenCardAssignees = (assignees = []) => {
  const allowedRoles = [
    AGENT_ROLES.isCustomerSuccessCoordinator,
    AGENT_ROLES.isCustomerSuccessTeamLead,
    AGENT_ROLES.isCustomerSuccessManager,
  ];

  return assignees.filter(
    (item) => item?.role && allowedRoles.includes(item.role)
  );
};

export const filterGreenCardCommentsUsers = (assignees = []) => {
  const allowedRoles = [
    AGENT_ROLES.isCustomerSuccessCoordinator,
    AGENT_ROLES.isCustomerSuccessTeamLead,
    AGENT_ROLES.isCustomerSuccessManager,
    AGENT_ROLES.isInsuranceSpecialist,
  ];

  return assignees.filter(
    (item) => item?.role && allowedRoles.includes(item.role)
  );
};

export const getGreenCardPayload = (fields = {}) => {
  const value = [
    {
      field: 'board_id',
      operator: FILTER_OPERATOR_ENUMS['='],
      type: FILTER_RULE_ENUMS.ComparableRuleType,
      value: BOARDS_ID.GREEN_CARD,
    },
  ];

  if (fields.assignee) {
    value.push({
      field: 'assignee',
      operator: 0,
      type: fields.assignee === 'none' ? FILTER_RULE_ENUMS.isEmpty : 5,
      value: fields.assignee === 'none' ? '' : fields.assignee,
    });
  }

  return {
    field: null,
    operator: null,
    type: FILTER_RULE_ENUMS.AndRuleType,
    value,
  };
};

const getControlledParking = (controlledParking) => {
  if (controlledParking === 'yes' || controlledParking === 'true') {
    return 'Yes';
  }
  if (controlledParking === 'no' || controlledParking === 'false') {
    return 'No';
  }
  return '-';
};

export const handleGreenCardData = (data = [], assignees = []) => {
  if (!data.length) {
    return {
      boardId: BOARDS_ID.GREEN_CARD,
      tasks: [],
    };
  }
  const tasks = [];

  data?.forEach((item) => {
    const assignee = assignees.find((a) => a.value === item.assignee) || {};

    const newTasks = {
      ...item,
      id: item?.id,
      date: item.customFields.endDate
        ? formatDate(item.customFields.endDate, 'dd/MM/yyyy')
        : null,
      href: `${URLS.greenCard}/${item?.id}`,
      requestFor: {
        text: `${item?.customFields?.userFullName} - ${item?.customFields?.vehicleDescription} ${item?.customFields?.vehiclePlate}`,
        value: item?.customFields?.userId,
      },
      description: `${item?.customFields?.userFullName} - ${item?.customFields?.vehicleDescription} ${item?.customFields?.vehiclePlate}`,
      status: item?.status,
      startDate: item?.customFields?.startDate
        ? formatDate(item?.customFields?.startDate)
        : '',
      endDate: item?.customFields?.endDate
        ? formatDate(item?.customFields?.endDate)
        : '',
      country: item?.customFields?.country || '-',
      controlledParking: getControlledParking(
        item?.customFields?.controlledParking
      ),
      rejectedBy: item?.customFields?.rejectedBy || '-',
      assignee,
      assigneeColumn: assignee?.text || '-',
      createdAt: item?.createdAt
        ? formatDate(item.createdAt, 'dd/MM/yyyy')
        : '-',
    };
    tasks.push(newTasks);
  });

  return {
    boardId: BOARDS_ID.GREEN_CARD,
    tasks,
  };
};

export const getGreenCardColumns = (tasks = []) => {
  const order = [0, 22, 23, 2, 25, 26, 3, 27, 12];
  const groupedByStatus = groupBy(tasks, 'status');

  const columns = order.reduce((acc, cur) => {
    acc.push({
      id: cur,
      title: GREEN_CARD_TASK_STATUSES_NAMES[cur].label,
      cards: groupedByStatus[cur] || [],
      styles: GREEN_CARD_TASK_STATUSES_NAMES[cur].styles,
      value: true,
      label: GREEN_CARD_TASK_STATUSES_NAMES[cur].label,
      key: cur,
    });
    return acc;
  }, []);

  return columns;
};

export const handleGreenCardTaskData = (data = {}, assignees = []) => {
  const task = {
    ...data,
    assignee: assignees.find((item) => item?.value === data?.assignee),
    changelog: data?.changelog?.toReversed(),
    customFields: {
      ...omit(data.customFields, ['comments']),
      country: data?.customFields?.country?.map((c) => ({ text: c, value: c })),
      mixedInsuranceExtensionPrice: {
        ...data?.customFields?.mixedInsuranceExtensionPrice,
        number: data?.customFields?.mixedInsuranceExtensionPrice?.number
          ? Number(data.customFields.mixedInsuranceExtensionPrice.number)
          : null,
      },
      customerCost: {
        ...data?.customFields?.customerCost,
        number: data?.customFields?.customerCost?.number
          ? Number(data.customFields.customerCost.number)
          : null,
      },
    },
    comments: sortArray(data?.comments || [], 'createdAt', 'desc')?.map(
      (entry) => {
        return {
          id: entry?.id,
          date: entry?.createdAt
            ? formatDate(entry?.createdAt, 'dd MMMM yyyy')
            : '-',
          time: entry?.createdAt ? formatDate(entry?.createdAt, 'HH:mm') : '-',
          name: entry?.userFullName || entry?.user || '-',
          comment: entry?.comment,
          edit: entry?.editedAt
            ? formatDate(entry?.editedAt, 'dd MMMM yyyy HH:mm')
            : '',
        };
      }
    ),
    documents: data?.documents || [],
  };
  return task;
};

export const handleVehicleData = (data) => {
  if (!data) {
    return {};
  }
  const properties = [];

  if (data.properties?.fuelType) {
    properties.push(data.properties.fuelType);
  }
  if (data.properties?.transmission) {
    properties.push(data.properties.transmission);
  }
  if (data.properties?.color) {
    properties.push(data.properties.color);
  }
  if (data.year) {
    properties.push(data.year);
  }

  return {
    internalId: data.internalId,
    id: data.id,
    title: [data.manufacturer, data.model].filter(Boolean).join(', '),
    plate: data.plate,
    image: data?.images?.[0]?.url,
    properties,
    vin: data.vin,
    procurement: data.procurement,
  };
};

export const getGreenCardParamsAndPayload = ({
  page,
  pageSize,
  orderBy,
  order,
  query,
  assignee,
}) => {
  const payload = getGreenCardPayload({
    query,
    assignee,
  });
  const cleanedParams = getCleanedUrlParams({
    page,
    pageSize,
    orderBy,
    order,
    query,
    assignee,
  });

  const requestUrlParams = getCleanedUrlParams({
    page,
    pageSize,
    orderBy,
    order,
    query,
  });

  const exportParams = omit(cleanedParams, ['page', 'pageSize', 'assignee']);

  return { params: cleanedParams, exportParams, payload, requestUrlParams };
};

export const getGreenCardTaskPayload = (values) => {
  const clearableFields = [];

  Object.keys(values)?.forEach((key) => {
    if (values[key] === '' && !clearableFields.includes(key)) {
      values[key] = null;
    }
  });

  const cleanedValues = omit(values, [
    'assignee',
    'changelog',
    'comments',
    'createdAt',
    'createdBy',
    'customFields.carId',
    'customFields.comments',
    'customFields.delivered',
    'customFields.rejectedReason',
    'customFields.servicePoint',
    'customFields.subscriptionId',
    'customFields.userFullName',
    'customFields.userId',
    'customFields.vehicleBrand',
    'customFields.vehicleDescription',
    'customFields.vehicleEdition',
    'customFields.vehicleFuel',
    'customFields.vehicleImage',
    'customFields.vehicleModel',
    'customFields.vehicleOwner',
    'customFields.vehiclePlate',
    'customFields.vehicleTransmission',
    'customFields.vehicleYear',
    'customFields.vin',
    'documents',
    'images',
    'name',
    'parent',
    'status',
    'tags',
    'updatedAt',
    'updatedBy',
  ]);

  return {
    assignee: values?.assignee?.value || null,
    customFields: {
      ...cleanedValues.customFields,
      country: cleanedValues.customFields?.country?.map((c) => c?.value),
      mixedInsuranceExtensionPrice: cleanedValues.customFields
        ?.mixedInsuranceExtensionPrice?.number
        ? `${cleanedValues.customFields?.mixedInsuranceExtensionPrice?.number}`
        : null,
      customerCost: cleanedValues.customFields?.customerCost?.number
        ? `${cleanedValues.customFields?.customerCost?.number}`
        : null,
    },
  };
};
